/* global $, APP, config */

import Logger from 'meet-hour-logger';
import React from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import { setObjectFit } from '../../../react/features/base/conference';
import { getActiveDOMDocument } from '../../../react/features/base/config/functions.any';
import { openDialog } from '../../../react/features/base/dialog';
import { i18next } from '../../../react/features/base/i18n';
import { MEDIA_TYPE } from '../../../react/features/base/media';
import {
    PARTICIPANT_ROLE,
    getLocalParticipant,
    getParticipantById,
    muteRemoteParticipant,
    participantVolumeChanged,
    pinParticipant
} from '../../../react/features/base/participants';
import { isTestModeEnabled } from '../../../react/features/base/testing';
import { getTrackByMediaTypeAndParticipant, updateLastTrackVideoMediaEvent } from '../../../react/features/base/tracks';
import { openChat, setIsPollsTabFocused } from '../../../react/features/chat/actions.web';
import { pollsTabFocusedHandler } from '../../../react/features/conference/actions.web';
import { Thumbnail, isVideoPlayable } from '../../../react/features/filmstrip';
import { RemoteVideoContextMenu, showRemoteVideoContextMenu } from '../../../react/features/filmstrip/components/web/RemoteVideoContextMenu';
import { close, closeParticipantsPaneModal } from '../../../react/features/participants-pane/actions';
import { LAYOUTS } from '../../../react/features/video-layout';
import { GrantModeratorDialog,
    KickRemoteParticipantDialog,
    MuteEveryoneDialog,
    MuteEveryonesVideoDialog,
    MuteRemoteParticipantDialog,
    MuteRemoteParticipantsVideoDialog
} from '../../../react/features/video-menu';
import { VolumeControlDialog } from '../../../react/features/video-menu/components/web';
import { REMOTE_CONTROL_MENU_STATES } from '../../../react/features/video-menu/components/web/RemoteControlButton';
import UIUtils from '../util/UIUtil';

import SmallVideo from './SmallVideo';

const logger = Logger.getLogger(__filename);

/**
 * List of container events that we are going to process, will be added as listener to the
 * container for every event in the list. The latest event will be stored in redux.
 */
const containerEvents = [
    'abort',
    'canplay',
    'canplaythrough',
    'emptied',
    'ended',
    'error',
    'loadeddata',
    'loadedmetadata',
    'loadstart',
    'pause',
    'play',
    'playing',
    'ratechange',
    'stalled',
    'suspend',
    'waiting'
];

/**
 * Determines whether a video element needs a fresh track attachment.
 *
 * @param {HTMLVideoElement} videoElement - The video element to check.
 * @param {MHTrack} videoTrack - The track expected to be attached.
 * @returns {boolean}
 */
function needsVideoElementReattach(videoElement, videoTrack) {
    if (!videoElement || !videoTrack) {
        return false;
    }

    const { srcObject } = videoElement;

    if (!srcObject) {
        return true;
    }

    const currentTracks
        = typeof srcObject.getVideoTracks === 'function'
            ? srcObject.getVideoTracks()
            : [];

    if (!currentTracks.length) {
        return true;
    }

    if (currentTracks.some(track => track?.readyState === 'ended')) {
        return true;
    }

    const expectedStreamId = videoTrack.stream?.id;

    return Boolean(expectedStreamId && srcObject.id !== expectedStreamId);
}

/**
 *
 * @param {*} spanId
 */
function createContainer(spanId) {
    const container = document.createElement('span');

    container.id = spanId;
    container.className = 'videocontainer';

    const state = APP.store.getState();
    const activeWindow = getActiveDOMDocument(state);

    const remoteVideosContainer = activeWindow?.getElementById(
        'filmstripRemoteVideosContainer'
    );
    const localVideoContainer = activeWindow?.getElementById(
        'localVideoTileViewContainer'
    );

    if (remoteVideosContainer && localVideoContainer) {
        remoteVideosContainer.insertBefore(container, localVideoContainer);
    } else {
        remoteVideosContainer?.appendChild(container);
    }

    return container;
}

/**
 *
 */
export default class RemoteVideo extends SmallVideo {
    /**
     * Handles volume change from VolumeSlider and synchronizes everywhere.
     * @param {number} newVolume
     */
    onVolumeChange = newVolume => {
        // Synchronize volume for this participant
        if (typeof participantVolumeChanged === 'function') {
            APP.store.dispatch(participantVolumeChanged(this.id, newVolume));
        }

        // Optionally, update Redux or local state if needed
    };

    /**
   * Creates new instance of the <tt>RemoteVideo</tt>.
   *
   * @param user - {MHParticipant} The user for whom remote video instance will
   * be created.
   * @class
   */
    constructor(user) {
        super();

        this.user = user;
        this.id = user.getId();
        this.videoSpanId = `participant_${this.id}`;
        this.reactRoot = null;
        this._disposed = false;
        this.addRemoteVideoContainer();

        // After container is created, force a re-render to ensure video element is created if track is present
        setTimeout(() => this.renderThumbnail(), 0);
        this.bindHoverHandler();
        this.flipX = false;
        this.isLocal = false;

        this.container.addEventListener('contextmenu', e => {
            showRemoteVideoContextMenu(e, this.videoSpanId);
        });

        /**
         * The flag is set to <tt>true</tt> after the 'canplay' event has been
         * triggered on the current video element. It goes back to <tt>false</tt>
         * when the stream is removed. It is used to determine whether the video
         * playback has ever started.
         *
         * @type {boolean}
         */
        this._canPlayEventReceived = false;

        this.container.onclick = this._onContainerClick;

        this.lastVideoTrackId = undefined;
        this._unsubscribe = APP.store.subscribe(() => {
            if (this._disposed) {
                return;
            }

            this._updateObjectFitStyle();
            this.updateZoomTransform();

            const state = APP.store.getState();
            const participant = getParticipantById(state, this.id);
            const tracksNow = state['features/base/tracks'];
            const trackNow = getTrackByMediaTypeAndParticipant(tracksNow, MEDIA_TYPE.VIDEO, this.id);
            const currentTrack = trackNow && trackNow.mhTrack;
            const currentVideoTrack = currentTrack || (participant && participant.videoTrack);
            const currentTrackId = currentVideoTrack
                && (typeof currentVideoTrack.getId === 'function'
                    ? currentVideoTrack.getId()
                    : currentVideoTrack.id);
            const videoElement = this.container && this.container.querySelector('video');
            const hasVideoElementNow = Boolean(videoElement);
            const expectedElementId = currentTrackId && `remoteVideo_${currentTrackId}`;
            const hasMismatchedElement = Boolean(videoElement && expectedElementId && videoElement.id !== expectedElementId);

            const hasDetachedElement = Boolean(
                videoElement
                    && (!videoElement.srcObject
                        || (typeof videoElement.srcObject.getVideoTracks === 'function'
                            && videoElement.srcObject.getVideoTracks().length === 0))
            );

            if (currentTrackId !== this.lastVideoTrackId
                || (currentTrack && (!hasVideoElementNow || hasMismatchedElement || hasDetachedElement))) {
                this.lastVideoTrackId = currentTrackId;
                this.renderThumbnail();
            }
        });

        // Ensure video element is created immediately if track is present
        this.renderThumbnail();
    }


    /**
     * Updates the objectFit style of the video element if it exists.
     */
    _updateObjectFitStyle() {
        // Find the video element for this participant
        const videoEl = this.container && this.container.querySelector('video');

        if (!videoEl) {
            return;
        }

        const state = APP.store.getState();
        const { filmStripView, objectFit } = state['features/base/conference'];

        videoEl.style.objectFit = filmStripView === LAYOUTS.TILE_VIEW ? objectFit : 'cover';
    }

    /**
   *
   */
    addRemoteVideoContainer() {
        this.container = createContainer(this.videoSpanId);
        this.$container = $(this.container);
        this.renderThumbnail();

        // this._setThumbnailSize();
        this.initBrowserSpecificProperties();

        return this.container;
    }

    /**
     * Renders the thumbnail.
     */
    renderThumbnail(isHovered = false) {
        // Do not (re)render after the instance has been removed. reactRoot.unmount()
        // flushes React synchronously and that commit can dispatch store actions
        // which re-enter this method via the store subscription; rendering then
        // would target a root that is being torn down and throw
        // "Cannot update an unmounted root".
        if (this._disposed || !this.container) {
            return;
        }

        if (!this.reactRoot) {
            this.reactRoot = createRoot(this.container); // Create root only once
        }

        // Get or create portal container for context menus
        let portalContainer = document.getElementById('context-menu-portal');

        if (!portalContainer) {
            portalContainer = document.createElement('div');
            portalContainer.id = 'context-menu-portal';
            document.body.appendChild(portalContainer);
        }

        this.reactRoot.render(
            <Provider store = { APP.store }>
                <I18nextProvider i18n = { i18next }>
                    <Thumbnail
                        isHovered = { isHovered }
                        participantID = { this.id } />
                    {createPortal(
                        <RemoteVideoContextMenu
                            participantId = { this.id }
                            showParticipantName = { true }
                            videoSpanId = { this.videoSpanId } />,
                        portalContainer
                    )}
                </I18nextProvider>
            </Provider>
        );

        // --- Ensure video element is present if video track is active ---
        const state = APP.store.getState();
        const participant = getParticipantById(state, this.id);
        const tracks = state['features/base/tracks'];
        const track = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, this.id);

        if (track && track.mhTrack) {
            const videoTrack = track.mhTrack;
            const expectedElementId = `remoteVideo_${videoTrack.getId()}`;
            let videoElement = this.container.querySelector('video');

            if (videoElement && videoElement.id !== expectedElementId) {
                logger.info('[RemoteVideo] Replacing stale video element with new track', {
                    participantId: this.id,
                    currentElementId: videoElement.id,
                    expectedElementId
                });
                videoElement.remove();
                videoElement = null;
            }

            if (videoElement && needsVideoElementReattach(videoElement, videoTrack)) {
                try {
                    videoTrack.attach(videoElement);
                } catch (error) {
                    logger.warn('[RemoteVideo] Failed to re-attach remote stream to existing element', {
                        participantId: this.id,
                        error
                    });
                    videoElement.remove();
                    videoElement = null;
                }
            }

            const hasVideoElement = Boolean(videoElement);

            if (!hasVideoElement && typeof this.addRemoteStreamElement === 'function') {
                this.addRemoteStreamElement(videoTrack);
            }
        } else {
            logger.warn('[RemoteVideo] No videoTrack for participant', {
                participantId: this.id,
                participant,
                tracks
            });
        }
    }

    /**
   * Removes the remote stream element corresponding to the given stream and
   * parent container.
   *
   * @param stream - The MediaStream.
   * @param isVideo - <tt>true</tt> if given <tt>stream</tt> is a video one.
   */
    removeRemoteStreamElement(stream) {
        if (!this.container) {
            return false;
        }

        const isVideo = stream.isVideoTrack();
        const elementID = `remoteVideo_${stream.getId()}`;
        const select = $(`#${elementID}`);

        select.remove();
        if (isVideo) {
            this._canPlayEventReceived = false;
        }

        this.updateView();
    }

    /**
   * The remote video is considered "playable" once the can play event has been received.
   *
   * @inheritdoc
   * @override
   */
    isVideoPlayable() {
        return (
            isVideoPlayable(APP.store.getState(), this.id)
      && this._canPlayEventReceived
        );
    }

    /**
   * @inheritDoc
   */
    updateView() {
        this.$container.toggleClass('audio-only', APP.conference.isAudioOnly());
        super.updateView();
    }

    /**
   * Removes RemoteVideo from the page.
   */
    remove() {
        this._disposed = true;

        // Unsubscribe from the store first. reactRoot.unmount() below flushes
        // React synchronously and that commit can dispatch actions which would
        // otherwise re-enter this subscriber and call renderThumbnail() on a
        // root that is in the middle of being unmounted.
        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = null;
        }

        if (this.reactRoot) {
            // Clear the reference before unmounting so that any re-entrant
            // renderThumbnail() call can never target this torn-down root.
            const root = this.reactRoot;

            this.reactRoot = null;
            root.unmount(); // Properly unmount before removing
        }

        // ReactDOM.unmountComponentAtNode(this.container);
        if (this.container) {
            this.container.innerHTML = '';
        }
        super.remove();
    }


    /**
         * Builds the context menu for the remote video.
         */
    _buildContextMenu() {
        const participantID = this.id; // Use this.id which is the participant ID
        const state = APP.store.getState();
        const participant = getParticipantById(state, participantID);
        const localParticipant = getLocalParticipant(state);
        const dispatch = APP.store.dispatch;
        const isMainModerator = state['features/base/jwt']?.user?.moderator || state['features/base/jwt']?.moderator;

        // Get remote control state
        const _isRemoteControlSessionActive = participant?.remoteControlSessionStatus ?? false;
        const _supportsRemoteControl = participant?.supportsRemoteControl ?? false;
        const { active, controller } = state['features/remote-control'];
        const { requestedParticipant, controlled } = controller;
        const activeParticipant = requestedParticipant || controlled;
        const { filmStripView } = state['features/base/conference'];
        const isPrivateChatDisabled = state['features/chat']?.isPrivateChatDisabled || false;

        let _remoteControlState = null;

        if (_supportsRemoteControl && ((!active && !_isRemoteControlSessionActive) || activeParticipant === participantID)) {
            if (requestedParticipant === participantID) {
                _remoteControlState = REMOTE_CONTROL_MENU_STATES.REQUESTING;
            } else if (controlled) {
                _remoteControlState = REMOTE_CONTROL_MENU_STATES.STARTED;
            } else {
                _remoteControlState = REMOTE_CONTROL_MENU_STATES.NOT_STARTED;
            }
        }

        // Check moderator status
        const _isModerator = Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR);

        // Get config settings
        const { remoteVideoMenu = {}, disableRemoteMute } = state['features/base/config'];
        const { disableKick } = remoteVideoMenu;

        // Build context menu items
        const contextMenuItems = {};

        // Check if participant is a fake participant
        const isFakeParticipant = participant?.isFakeParticipant ?? false;


        // Add Private Message option (skip for fake participants)
        if (!isFakeParticipant && !isPrivateChatDisabled) {
            contextMenuItems.privateMessage = {
                name: 'Send Private Message',
                callback: () => {
                    // Open chat with private message to this participant
                    dispatch(openChat(participant));
                    dispatch(closeParticipantsPaneModal());
                    dispatch(close());
                    dispatch(pollsTabFocusedHandler(false));
                    dispatch(setIsPollsTabFocused(false));
                }
            };
        }

        contextMenuItems.objectFit = {
            name: 'Fit / Strech Video',
            callback: () => {
                const stateNow = APP.store.getState();
                const currentObjectFit = stateNow['features/base/conference'].objectFit || 'cover';

                dispatch(setObjectFit(currentObjectFit === 'cover' ? 'contain' : 'cover'));// Get the latest state and participant
            }
        };

        // Add Pin/Unpin option
        filmStripView === LAYOUTS.TILE_VIEW && (contextMenuItems.pin = {
            name: 'Pin / Unpin Participant',
            callback: () => {
                dispatch(pinParticipant(participantID));
            }
        });

        // Add Volume Control option (skip for fake participants)
        if (!isFakeParticipant) {
            contextMenuItems.volumeControl = {
                name: 'Volume Control',
                callback: () => {
                    dispatch(openDialog(VolumeControlDialog, {
                        participantID
                    }));
                }
            };
        }

        // Add Moderator-only options
        if (_isModerator && !isFakeParticipant) {
            // Add Mute option (if not disabled)
            if (!disableRemoteMute) {
                const isAudioMuted = participant?.audioMuted ?? false;

                contextMenuItems.muteAudio = {
                    name: isAudioMuted ? 'Unmute Audio' : 'Mute Video',
                    callback: () => {
                        if (isAudioMuted) {
                            // Use proper action for unmuting - this would need to be implemented
                            dispatch(muteRemoteParticipant(participantID, MEDIA_TYPE.AUDIO));
                        } else {
                            dispatch(openDialog(MuteRemoteParticipantDialog, { participantID }));
                        }
                    }
                };

                // Add Video Mute option
                const isVideoMuted = participant?.videoMuted ?? false;

                contextMenuItems.muteVideo = {
                    name: isVideoMuted ? 'Unmute Video' : 'Mute Video',
                    callback: () => {
                        if (isVideoMuted) {
                            // Use proper action for unmuting video - this would need to be implemented
                            dispatch(muteRemoteParticipant(participantID, MEDIA_TYPE.VIDEO));
                        } else {
                            dispatch(openDialog(MuteRemoteParticipantsVideoDialog, { participantID }));
                        }
                    }
                };
            }

            // Add Grant/Revoke Moderator option
            const isTargetModerator = participant?.role === PARTICIPANT_ROLE.MODERATOR;

            if (isMainModerator) {

                contextMenuItems.grantModerator = {
                    name: isTargetModerator ? 'Revoke Moderator' : 'Grant Moderator',
                    callback: () => {
                        dispatch(openDialog(GrantModeratorDialog, { participantID }));
                    }
                };
            }

            // Add Mute Everyone Else option
            contextMenuItems.muteEveryoneElse = {
                name: 'Mute Everyone Else',
                callback: () => {
                    dispatch(openDialog(MuteEveryoneDialog, { exclude: [ participantID ] }));
                }
            };

            // Add Mute Everyone Else's Video option
            contextMenuItems.muteEveryoneElseVideo = {
                name: 'Mute Everyone Else\'s Video',
                callback: () => {
                    dispatch(openDialog(MuteEveryonesVideoDialog, { exclude: [ participantID ] }));
                }
            };

            // Add Kick option (if not disabled)
            if (!disableKick) {
                contextMenuItems.kick = {
                    name: 'Remove Participant',
                    callback: () => {
                        dispatch(openDialog(KickRemoteParticipantDialog, { participantID }));
                    }
                };
            }
        }

        $.contextMenu({
            selector: `#${this.videoSpanId}`,
            zIndex: 10000,
            items: contextMenuItems,
            events: {
                show(options) {
                    // Update translations for all items
                    if (options.items.remoteControl) {
                        // Update remote control text based on current state
                        const currentState = _remoteControlState;

                        if (currentState === REMOTE_CONTROL_MENU_STATES.STARTED) {
                            options.items.remoteControl.name = APP.translation.generateTranslationHTML('toolbar.stopRemoteControlText');
                        } else if (currentState === REMOTE_CONTROL_MENU_STATES.NOT_STARTED) {
                            options.items.remoteControl.name = APP.translation.generateTranslationHTML('toolbar.requestRemoteControlText');
                        }
                    }
                    if (options.items.privateMessage) {
                        options.items.privateMessage.name = APP.translation.generateTranslationHTML('toolbar.privateMessage');
                    }

                    if (options.items.objectFit) {
                        options.items.objectFit.name = APP.translation.generateTranslationHTML('toolbar.objectFit');
                    }

                    if (options.items.pin) {
                        const currentlyPinned = participant?.pinned || false;

                        options.items.pin.name = APP.translation.generateTranslationHTML(
                            currentlyPinned ? 'videothumbnail.unpin' : 'videothumbnail.pin'
                        );
                    }
                    if (options.items.volumeControl) {
                        options.items.volumeControl.name = APP.translation.generateTranslationHTML('volumeSlider.label');
                    }
                    if (options.items.muteAudio) {
                        const isAudioMuted = participant?.audioMuted ?? false;

                        options.items.muteAudio.name = APP.translation.generateTranslationHTML(
                            isAudioMuted ? 'videothumbnail.unmute' : 'videothumbnail.domute'
                        );
                    }
                    if (options.items.muteVideo) {
                        const isVideoMuted = participant?.videoMuted ?? false;

                        options.items.muteVideo.name = APP.translation.generateTranslationHTML(
                            isVideoMuted ? 'videothumbnail.unmuteVideo' : 'videothumbnail.domuteVideo'
                        );
                    }
                    if (options.items.grantModerator) {
                        const isTargetModerator = participant?.role === PARTICIPANT_ROLE.MODERATOR;

                        options.items.grantModerator.name = APP.translation.generateTranslationHTML(
                            isTargetModerator ? 'dialog.revokeModerationTitle' : 'videothumbnail.grantModerator'
                        );
                    }
                    if (options.items.muteEveryoneElse) {
                        options.items.muteEveryoneElse.name = APP.translation.generateTranslationHTML('videothumbnail.domuteOthers');
                    }
                    if (options.items.muteEveryoneElseVideo) {
                        options.items.muteEveryoneElseVideo.name = APP.translation.generateTranslationHTML('videothumbnail.domuteVideoOfOthers');
                    }
                    if (options.items.kick) {
                        options.items.kick.name = APP.translation.generateTranslationHTML('videothumbnail.kick');
                    }
                }
            }
        });
    }


    /**
   *
   * @param {*} streamElement
   * @param {*} stream
   */
    waitForPlayback(streamElement, stream) {
        $(streamElement).hide();

        const webRtcStream = stream.getOriginalStream();
        const isVideo = stream.isVideoTrack();

        if (!isVideo || webRtcStream.id === 'mixedmslabel') {
            return;
        }

        const listener = () => {
            this._canPlayEventReceived = true;

            if (streamElement) {
                $(streamElement).show();
            }

            streamElement.removeEventListener('canplay', listener);
            if (this._pendingCanPlay?.listener === listener) {
                this._pendingCanPlay = undefined;
            }

            // Refresh to show the video
            this.updateView();
            this.updateZoomTransform();
        };

        // The same <video> element is reused for every stream attached to this tile, so the listeners from the
        // previous attach have to be taken off it. Otherwise each re-attach (track replaced, unmute, reconnect)
        // left one more permanent loadedmetadata handler - and a canplay one if it had not fired yet - all
        // holding this thumbnail.
        if (this._pendingCanPlay) {
            this._pendingCanPlay.element.removeEventListener('canplay', this._pendingCanPlay.listener);
        }
        this._pendingCanPlay = {
            element: streamElement,
            listener
        };
        streamElement.addEventListener('canplay', listener);

        if (!this._onLoadedMetadata) {
            this._onLoadedMetadata = () => this.updateZoomTransform();
        }
        streamElement.removeEventListener('loadedmetadata', this._onLoadedMetadata);
        streamElement.addEventListener('loadedmetadata', this._onLoadedMetadata);
    }

    /**
   *
   * @param {*} stream
   */
    addRemoteStreamElement(stream) {

        if (!this.container) {
            logger.debug('[RemoteVideo] Not attaching remote stream due to no container', this.id, stream);

            return;
        }

        const isVideo = stream.isVideoTrack();

        logger.debug('[RemoteVideo] addRemoteStreamElement stream:', this.id, stream);
        logger.debug('[RemoteVideo] addRemoteStreamElement getOriginalStream:', this.id, stream.getOriginalStream && stream.getOriginalStream());

        if (!stream.getOriginalStream()) {
            logger.debug('[RemoteVideo] Remote video stream has no original stream', this.id, stream);

            return;
        }

        const expectedElementId = `remoteVideo_${stream.getId()}`;
        const existingVideoElement = this.container.querySelector('video');

        // Keep existing element only if it already belongs to the same track.
        if (existingVideoElement && existingVideoElement.id === expectedElementId
            && !needsVideoElementReattach(existingVideoElement, stream)) {
            logger.debug('[RemoteVideo] Remote video element already exists for stream', this.id, stream.getId());

            return;
        }

        if (existingVideoElement) {
            existingVideoElement.id = expectedElementId;
            this.waitForPlayback(existingVideoElement, stream);

            try {
                stream.attach(existingVideoElement);
            } catch (error) {
                logger.warn('[RemoteVideo] Failed to attach stream to existing video element, recreating element', {
                    participantId: this.id,
                    error
                });
                existingVideoElement.remove();
            }

            if (this.container.querySelector(`#${expectedElementId}`) === existingVideoElement) {
                this.updateZoomTransform();

                return;
            }
        }

        let streamElement = document.createElement('video');

        streamElement.autoplay = !config.testing?.noAutoPlayVideo;
        streamElement.id = `remoteVideo_${stream.getId()}`;
        streamElement.muted = true;
        streamElement.playsInline = true;

        // Put new stream element always in front
        streamElement = UIUtils.prependChild(this.container, streamElement);

        this.waitForPlayback(streamElement, stream);
        stream.attach(streamElement);
        this.updateZoomTransform();

        // Start playback eagerly for visible tiles to reduce first-frame latency
        // across browsers; canplay/playing guards still handle readiness.
        if (this.container.style.display !== 'none') {
            streamElement.play().catch(() => {
                // Ignore autoplay interruptions; normal playback path will continue.
            });
        }

        if (isVideo && isTestModeEnabled(APP.store.getState())) {
            const cb = name => dispatch(updateLastTrackVideoMediaEvent(stream, name));

            containerEvents.forEach(event => {
                streamElement.addEventListener(event, cb.bind(this, event));
            });
        }
    }
}
